"use client";

import { useState, useRef, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ar, enUS } from "date-fns/locale";
import { format } from "date-fns";

type Props = {
  value?: Date;
  onChange: (date: Date) => void;
  locale: "ar" | "en";
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  disabled?: boolean;
};

export default function DateInput({
  value,
  onChange,
  locale,
  minDate,
  maxDate,
  placeholder,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const endMonth = maxDate ?? new Date();
  const startMonth = minDate ?? new Date(1930, 0);
  const defaultMonth = new Date(1995, 0, 1);

  // close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () =>
      document.removeEventListener("mousedown", handleClick);
  }, []);

  const dateLocale = locale === "ar" ? ar : enUS;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* input */}
      <input
        readOnly
        disabled={disabled}
        className="w-full h-11 px-3 text-sm rounded-lg border bg-input text-foreground placeholder:text-text-muted transition-colors duration-150 focus:outline-none border-border focus:border-primary focus:ring-2 focus:ring-focus-ring/50 disabled:bg-hover disabled:text-text-muted disabled:cursor-not-allowed disabled:border-border-subtle"
        placeholder={placeholder}
        value={
          value
            ? format(
                value,
                locale === "ar" ? "dd/MM/yyyy" : "yyyy-MM-dd",
                { locale: dateLocale }
              )
            : ""
        }
        onClick={() => !disabled && setOpen((prev) => !prev)}
      />

      {/* calendar */}
      {open && !disabled && (
        <div
          // Same surface treatment as the select menus (FormSelect's `menu`
          // style), so a calendar and a dropdown opening from adjacent fields
          // read as the same control. The shadow is deliberately softer than
          // the previous hardcoded black, which was heavy on the light theme.
          className="absolute z-[999] mt-1.5 rounded-[10px] border border-border bg-surface-elevated p-2.5 shadow-[0_10px_30px_rgba(15,23,42,0.12)]"
        >
          <DayPicker
            mode="single"
            selected={value ?? undefined}
            locale={dateLocale}
            dir={locale === "ar" ? "rtl" : "ltr"}
            captionLayout="dropdown"
            navLayout="after"
            reverseYears
            startMonth={startMonth}
            endMonth={endMonth}
            defaultMonth={value ?? defaultMonth}
            disabled={(date) => {
              if (minDate && date < minDate) return true;
              if (maxDate && date > maxDate) return true;

              return false;
            }}
            onSelect={(date) => {
              if (!date) return;

              const safeDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate()
              );

              onChange(safeDate);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
